import { TaglineBadge } from '@/components/brand/TaglineBadge';
import { usePublicFormSubmission } from '@/components/page-builder/forms/usePublicFormSubmission';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const EASE = [0.22, 1, 0.36, 1] as const;

// Shared input styling — identical to AgentApplyForm so both forms match.
const INPUT_CLS =
    'lux-body lux-body-sm border-[#F5F5F5]/30 bg-transparent text-[#F5F5F5] transition-colors duration-200 placeholder:text-[#F5F5F5]/55 focus-visible:border-[#F5F5F5]/55 focus-visible:ring-[#F5F5F5]/10';
const LABEL_CLS = 'lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70';

const REVENUE_OPTIONS = ['Under $500K', '$500K – $1M', '$1M – $3M', '$3M – $5M', '$5M – $10M', '$10M – $50M', '$50M – $100M', '$100M+'];
const ROLE_OPTIONS = ['Agency Owner', 'Established Advisor'];

/* Chris Cardena — VP of Business Development. Verbatim from the LE Miami invite
 * template + sales one-pager (design 130). */
const CHRIS = {
    firstName: 'Chris',
    lastName: 'Cardena',
    title: 'VP of Business Development',
    org: 'LuxRally Travel',
    phone: '+17544574478',
    phoneDisplay: '+1 (754) 457-4478',
    email: 'chris.cardena@luxrallytravel.com',
    image: 'https://cdnweb.luxrallytravel.com/2026/04/bffb1aac-f8c6-43f1-9d3a-11a3e4b7250a.jpeg',
};

// Raw vCard payload (BEGIN:VCARD…) — phone scanners reject data: URIs, so the QR
// encodes this string directly (matches the /the-team/{slug} save-contact).
function chrisVCard(): string {
    return [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `FN:${CHRIS.firstName} ${CHRIS.lastName}`,
        `N:${CHRIS.lastName};${CHRIS.firstName};;;`,
        `TITLE:${CHRIS.title}`,
        `ORG:${CHRIS.org}`,
        `TEL;TYPE=CELL:${CHRIS.phone}`,
        `EMAIL;TYPE=WORK:${CHRIS.email}`,
        'END:VCARD',
    ].join('\n');
}
const CHRIS_VCARD_URI = `data:text/vcard;charset=utf-8,${encodeURIComponent(chrisVCard())}`;

const EMPTY = { name: '', phone: '', email: '', agency_name: '', role: '', revenue: '', message: '', _hp_field: '' };

export default function AgencyConnect() {
    const [open, setOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    const [fields, setFields] = useState({ ...EMPTY });
    const { submitForm, processing } = usePublicFormSubmission();
    const fid = useId();

    const isMobile = useMemo(() => typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent), []);
    const close = useCallback(() => setOpen(false), []);

    // Open in response to a page-wide trigger (e.g. the "Request a Live Demo" button).
    useEffect(() => {
        const openHandler = () => setOpen(true);
        window.addEventListener('agency-connect:open', openHandler);
        return () => window.removeEventListener('agency-connect:open', openHandler);
    }, []);

    // Esc to close + lock background scroll while the modal is open.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && (qrOpen ? setQrOpen(false) : close());
        window.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [open, qrOpen, close]);

    const setField = (k: keyof typeof EMPTY, v: string) => setFields((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const { _hp_field, ...formData } = fields;
        await submitForm({
            formId: 'agency-connect',
            pageId: typeof window !== 'undefined' ? window.location.pathname : '/become-an-agent',
            // Recipients are set server-side (config/forms.php) — not sent from here.
            formData,
            replyTo: fields.email,
            honeypot: _hp_field,
            onSuccess: () => setSubmitted(true),
        });
    };

    const handleSaveContact = () => {
        if (isMobile) {
            const a = document.createElement('a');
            a.href = CHRIS_VCARD_URI;
            a.download = 'Chris-Cardena.vcf';
            document.body.appendChild(a);
            a.click();
            a.remove();
        } else {
            setQrOpen(true);
        }
    };

    return (
        <>
            {/* ── "Connect with Chris" card under the hero ── */}
            <section className="agent-px bg-[#f7f4ec] py-16 sm:py-20">
                <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 sm:flex-row sm:items-stretch sm:justify-center sm:gap-9">
                    {/* Identity — circle photo + name/title inline */}
                    <div className="flex shrink-0 items-center justify-center gap-4">
                        <img
                            src={CHRIS.image}
                            alt={`${CHRIS.firstName} ${CHRIS.lastName}`}
                            className="h-20 w-20 shrink-0 rounded-full object-cover object-top ring-1 ring-[#0e2727]/15"
                        />
                        <div className="text-left">
                            <p className="lux-body lux-body-lg !font-medium text-[#0e2727]">
                                {CHRIS.firstName} {CHRIS.lastName}
                            </p>
                            <p className="lux-caps lux-caps-sm mt-1 text-[#0e2727]/55">VP, Business Development</p>
                        </div>
                    </div>

                    {/* Kicker + pitch + CTA — stacked */}
                    <div className="flex w-full flex-col items-center gap-4 border-t border-[#0e2727]/12 pt-6 text-center sm:w-auto sm:max-w-xl sm:items-start sm:justify-center sm:border-t-0 sm:border-l sm:pt-0 sm:pl-9 sm:text-left">
                        <TaglineBadge
                            text="AGENCY OWNERS & $1M+ ADVISORS"
                            className="block h-[10px] w-auto max-w-full text-[#0e2727]/60"
                        />
                        <p className="lux-didot lux-didot-md text-[#0e2727]">
                            Skip the application. Start with a direct conversation about bringing your business to LuxRally.
                        </p>
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="lux-caps lux-caps-sm inline-flex shrink-0 items-center rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-5 py-2.5 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020]"
                        >
                            Connect with Chris
                        </button>
                    </div>
                </div>
            </section>

            {/* ── Modal: form / success ── */}
            {open && (
                <div className="fixed inset-0 z-[9999] overflow-y-auto bg-[#0b2222]">
                    <button
                        type="button"
                        onClick={close}
                        aria-label="Close"
                        className="fixed top-6 right-6 z-[10000] flex h-10 w-10 items-center justify-center rounded-full text-[#F5F5F5]/60 transition-colors hover:bg-[#F5F5F5]/10 hover:text-[#F5F5F5]"
                    >
                        <X className="h-5 w-5" strokeWidth={1.5} />
                    </button>

                    <div className="agent-px flex min-h-full items-start justify-center pt-20 pb-16 md:items-center md:pt-16">
                        <div className="w-full max-w-3xl">
                            {!submitted ? (
                                <form onSubmit={onSubmit} className="space-y-4">
                                    <div className="mb-8 text-center">
                                        <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">Connect with our VP of Business Development.</h2>
                                        <TaglineBadge
                                            text="FOR AGENCY OWNERS & ESTABLISHED ADVISORS"
                                            className="mx-auto mt-5 h-[9px] w-auto max-w-full text-[#F5F5F5]/50"
                                        />
                                    </div>

                                    {/* Honeypot */}
                                    <div className="absolute -left-[9999px]" aria-hidden="true">
                                        <input
                                            type="text"
                                            name="_hp_field"
                                            tabIndex={-1}
                                            autoComplete="off"
                                            value={fields._hp_field}
                                            onChange={(e) => setField('_hp_field', e.target.value)}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor={`${fid}-name`} className={LABEL_CLS}>
                                                Name
                                            </label>
                                            <Input
                                                id={`${fid}-name`}
                                                value={fields.name}
                                                onChange={(e) => setField('name', e.target.value)}
                                                className={INPUT_CLS}
                                                autoComplete="name"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`${fid}-phone`} className={LABEL_CLS}>
                                                Phone
                                            </label>
                                            <PhoneInput
                                                id={`${fid}-phone`}
                                                value={fields.phone}
                                                onChange={(val) => setField('phone', val ?? '')}
                                                defaultCountry="US"
                                                buttonClassName="border-[#F5F5F5]/30 bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/5"
                                                buttonStyle={{ color: '#F5F5F5' }}
                                                inputClassName={INPUT_CLS}
                                                popoverClassName="z-[10001]"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor={`${fid}-email`} className={LABEL_CLS}>
                                                Email
                                            </label>
                                            <Input
                                                id={`${fid}-email`}
                                                type="email"
                                                value={fields.email}
                                                onChange={(e) => setField('email', e.target.value)}
                                                className={INPUT_CLS}
                                                autoComplete="email"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`${fid}-agency`} className={LABEL_CLS}>
                                                Agency Name
                                            </label>
                                            <Input
                                                id={`${fid}-agency`}
                                                value={fields.agency_name}
                                                onChange={(e) => setField('agency_name', e.target.value)}
                                                className={INPUT_CLS}
                                                autoComplete="organization"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor={`${fid}-role`} className={LABEL_CLS}>
                                                Are you an agency owner or advisor?
                                            </label>
                                            <Select value={fields.role} onValueChange={(v) => setField('role', v)} required>
                                                <SelectTrigger id={`${fid}-role`} className={`${INPUT_CLS} data-[placeholder]:text-[#F5F5F5]/55`}>
                                                    <SelectValue placeholder="Select one" />
                                                </SelectTrigger>
                                                <SelectContent className="z-[10001]">
                                                    {ROLE_OPTIONS.map((o) => (
                                                        <SelectItem key={o} value={o}>
                                                            {o}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <label htmlFor={`${fid}-revenue`} className={LABEL_CLS}>
                                                Annual Revenue
                                            </label>
                                            <Select value={fields.revenue} onValueChange={(v) => setField('revenue', v)} required>
                                                <SelectTrigger id={`${fid}-revenue`} className={`${INPUT_CLS} data-[placeholder]:text-[#F5F5F5]/55`}>
                                                    <SelectValue placeholder="Select a range" />
                                                </SelectTrigger>
                                                <SelectContent className="z-[10001]">
                                                    {REVENUE_OPTIONS.map((o) => (
                                                        <SelectItem key={o} value={o}>
                                                            {o}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor={`${fid}-message`} className={LABEL_CLS}>
                                            Message
                                        </label>
                                        <Textarea
                                            id={`${fid}-message`}
                                            rows={4}
                                            value={fields.message}
                                            onChange={(e) => setField('message', e.target.value)}
                                            className={`${INPUT_CLS} resize-none dark:bg-transparent`}
                                        />
                                    </div>

                                    <div className="mt-8 mb-10 flex justify-center">
                                        <button
                                            type="submit"
                                            disabled={processing}
                                            className="lux-caps lux-caps-sm h-auto w-full cursor-pointer rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-4 py-2.5 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020] focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/60 focus-visible:outline-none disabled:opacity-50 sm:w-auto sm:py-1"
                                        >
                                            {processing ? 'Sending…' : 'Send to Chris'}
                                        </button>
                                    </div>

                                    {/* Trust marks — same block as the agent application form */}
                                    <div className="flex items-center justify-center gap-6">
                                        <img
                                            src="/EndorsedByLogo-FTG-REV.png"
                                            alt="Forbes Travel Guide Endorsed"
                                            className="h-9 w-auto object-contain opacity-50"
                                        />
                                        <img src="/iata.svg" alt="IATA" className="h-6 w-auto object-contain opacity-50 brightness-0 invert" />
                                        <img
                                            src="/TLN-logo.png"
                                            alt="Travel Leaders Network"
                                            className="h-6 w-auto object-contain opacity-50 brightness-0 invert"
                                        />
                                        <img src="/arc.png" alt="ARC" className="h-6 w-auto object-contain opacity-50 brightness-0 invert" />
                                    </div>
                                </form>
                            ) : (
                                <div className="text-center">
                                    <img
                                        src={CHRIS.image}
                                        alt={`${CHRIS.firstName} ${CHRIS.lastName}`}
                                        className="mx-auto mb-6 h-24 w-24 rounded-full object-cover ring-1 ring-[#F5F5F5]/15"
                                    />
                                    <h2 className="lux-didot lux-didot-lg mb-3 text-[#F5F5F5]">Chris will reach out shortly.</h2>
                                    <p className="lux-body lux-body-sm mx-auto mb-10 max-w-md leading-relaxed text-[#F5F5F5]/60">
                                        Thank you — your details are with our VP of Business Development, {CHRIS.firstName} {CHRIS.lastName}. Expect a
                                        personal reply soon.
                                    </p>

                                    <div className="mx-auto max-w-sm border-t border-[#F5F5F5]/15 pt-8">
                                        <span className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/45">Need to speak urgently?</span>
                                        <p className="lux-body lux-body-sm mb-5 leading-relaxed text-[#F5F5F5]/60">
                                            Save {CHRIS.firstName}&rsquo;s contact and reach him directly.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleSaveContact}
                                            className="inline-flex items-center rounded-sm border border-[#F5F5F5]/30 px-5 py-2.5 transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#F5F5F5]/5"
                                        >
                                            <TaglineBadge text="SAVE CONTACT" leadingIcon="save-contact" className="h-3 w-auto text-[#F5F5F5]/70" />
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={close}
                                        className="lux-caps lux-caps-sm mt-10 text-[#F5F5F5]/50 transition-colors hover:text-[#F5F5F5]"
                                    >
                                        Close
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Save Contact — QR modal (desktop), mirrors /the-team/{slug} ── */}
            {typeof document !== 'undefined' &&
                createPortal(
                    <AnimatePresence>
                        {qrOpen && (
                            <>
                                <motion.div
                                    key="qr-backdrop"
                                    className="fixed inset-0 z-[10010] bg-black/60 backdrop-blur-sm"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    onClick={() => setQrOpen(false)}
                                />
                                <motion.div
                                    key="qr-modal"
                                    className="fixed inset-0 z-[10010] flex items-center justify-center p-4"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.3, ease: EASE }}
                                >
                                    <div
                                        className="relative w-full max-w-sm rounded-sm bg-[#0e2727] p-10 text-center shadow-2xl"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setQrOpen(false)}
                                            className="absolute top-4 right-4 text-[#F5F5F5]/40 transition-colors hover:text-[#F5F5F5]"
                                            aria-label="Close"
                                        >
                                            <X className="h-5 w-5" strokeWidth={1.5} />
                                        </button>
                                        <h3 className="lux-didot lux-didot-md text-[#F5F5F5]">Save {CHRIS.firstName}&rsquo;s Contact</h3>
                                        <p className="lux-body lux-body-sm mt-2 text-[#F5F5F5]/60">Scan with your phone to add to contacts</p>
                                        <div className="mx-auto mt-8 mb-8 inline-block rounded-sm bg-white p-4">
                                            <QRCodeSVG value={chrisVCard()} size={200} level="M" bgColor="#ffffff" fgColor="#0e2727" />
                                        </div>
                                        <p className="lux-body lux-body-sm text-[#F5F5F5]/60">
                                            or{' '}
                                            <a
                                                href={CHRIS_VCARD_URI}
                                                download="Chris-Cardena.vcf"
                                                className="!text-[#F5F5F5] underline transition-colors hover:!text-[#F5F5F5]"
                                            >
                                                download the contact file
                                            </a>
                                        </p>
                                    </div>
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>,
                    document.body,
                )}
        </>
    );
}
